import React, { useState } from 'react';
import { FileTextIcon, PillIcon, FlaskConicalIcon, AlertCircleIcon, ClipboardIcon } from 'lucide-react';
interface MedicalHistoryProps {
  patientId: number;
}
const mockAllergies = [{
  id: 1,
  allergen: 'Penicillin',
  reaction: 'Rash',
  severity: 'Moderate',
  dateIdentified: '2020-03-15'
}, {
  id: 2,
  allergen: 'Peanuts',
  reaction: 'Anaphylaxis',
  severity: 'Severe',
  dateIdentified: '2015-07-22'
}];
const mockMedicalHistory = [{
  id: 1,
  condition: 'Hypertension',
  diagnosedDate: '2018-05-10',
  status: 'Active',
  notes: 'Controlled with medication'
}, {
  id: 2,
  condition: 'Type 2 Diabetes',
  diagnosedDate: '2019-11-20',
  status: 'Active',
  notes: 'Managing with diet and medication'
}];
const mockVisitNotes = [{
  id: 1,
  date: '2024-01-10',
  doctor: 'Dr. Sarah Johnson',
  complaint: 'Regular check-up',
  diagnosis: 'Healthy',
  notes: 'Blood pressure normal, continue current medications',
  followUp: '2024-04-10'
}, {
  id: 2,
  date: '2023-12-05',
  doctor: 'Dr. Michael Chen',
  complaint: 'Headache',
  diagnosis: 'Migraine',
  notes: 'Prescribed pain medication, advised rest',
  followUp: '2024-01-05'
}];
const mockPrescriptions = [{
  id: 1,
  date: '2024-01-10',
  medication: 'Lisinopril 10mg',
  dosage: '1 tablet daily',
  duration: '3 months',
  prescribedBy: 'Dr. Sarah Johnson',
  status: 'Active'
}, {
  id: 2,
  date: '2024-01-10',
  medication: 'Metformin 500mg',
  dosage: '2 tablets twice daily',
  duration: '3 months',
  prescribedBy: 'Dr. Sarah Johnson',
  status: 'Active'
}];
const mockLabResults = [{
  id: 1,
  date: '2024-01-08',
  testName: 'Complete Blood Count',
  result: 'Normal',
  performedBy: 'City Lab',
  notes: 'All parameters within normal range'
}, {
  id: 2,
  date: '2024-01-08',
  testName: 'HbA1c',
  result: '6.2%',
  performedBy: 'City Lab',
  notes: 'Good diabetes control'
}, {
  id: 3,
  date: '2023-12-01',
  testName: 'Lipid Panel',
  result: 'Borderline High',
  performedBy: 'City Lab',
  notes: 'LDL slightly elevated'
}];
export function MedicalHistory({
  patientId
}: MedicalHistoryProps) {
  const [activeSection, setActiveSection] = useState<'allergies' | 'history' | 'visits' | 'prescriptions' | 'labs'>('allergies');
  return <div className="space-y-6" data-id="element-1221">
      {/* Section Selector */}
      <div className="flex flex-wrap gap-2" data-id="element-1222">
        <button onClick={() => setActiveSection('allergies')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'allergies' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1223">
          <AlertCircleIcon className="w-4 h-4 inline mr-2" data-id="element-1224" />
          Allergies
        </button>
        <button onClick={() => setActiveSection('history')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'history' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1225">
          <FileTextIcon className="w-4 h-4 inline mr-2" data-id="element-1226" />
          Medical History
        </button>
        <button onClick={() => setActiveSection('visits')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'visits' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1227">
          <ClipboardIcon className="w-4 h-4 inline mr-2" data-id="element-1228" />
          Visit Notes
        </button>
        <button onClick={() => setActiveSection('prescriptions')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'prescriptions' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1229">
          <PillIcon className="w-4 h-4 inline mr-2" data-id="element-1230" />
          Prescriptions
        </button>
        <button onClick={() => setActiveSection('labs')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'labs' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1231">
          <FlaskConicalIcon className="w-4 h-4 inline mr-2" data-id="element-1232" />
          Lab Results
        </button>
      </div>
      {/* Allergies */}
      {activeSection === 'allergies' && <div className="space-y-4" data-id="element-1233">
          <div className="flex items-center justify-between" data-id="element-1234">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1235">Allergies</h3>
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm" data-id="element-1236">
              Add Allergy
            </button>
          </div>
          {mockAllergies.map(allergy => <div key={allergy.id} className="border border-red-200 bg-red-50 rounded-lg p-4" data-id="element-1237">
              <div className="flex items-start justify-between" data-id="element-1238">
                <div className="flex-1" data-id="element-1239">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-1240">
                    <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1241" />
                    <h4 className="font-semibold text-gray-900" data-id="element-1242">
                      {allergy.allergen}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${allergy.severity === 'Severe' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'}`} data-id="element-1243">
                      {allergy.severity}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-1244">
                    <p data-id="element-1245">
                      <strong data-id="element-1246">Reaction:</strong> {allergy.reaction}
                    </p>
                    <p data-id="element-1247">
                      <strong data-id="element-1248">Identified:</strong> {allergy.dateIdentified}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>}
      {/* Medical History */}
      {activeSection === 'history' && <div className="space-y-4" data-id="element-1249">
          <div className="flex items-center justify-between" data-id="element-1250">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1251">
              Medical History
            </h3>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-1252">
              Add Condition
            </button>
          </div>
          {mockMedicalHistory.map(condition => <div key={condition.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1253">
              <div className="flex items-start justify-between mb-2" data-id="element-1254">
                <h4 className="font-semibold text-gray-900" data-id="element-1255">
                  {condition.condition}
                </h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${condition.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1256">
                  {condition.status}
                </span>
              </div>
              <div className="text-sm text-gray-700" data-id="element-1257">
                <p data-id="element-1258">
                  <strong data-id="element-1259">Diagnosed:</strong> {condition.diagnosedDate}
                </p>
                <p data-id="element-1260">
                  <strong data-id="element-1261">Notes:</strong> {condition.notes}
                </p>
              </div>
            </div>)}
        </div>}
      {/* Visit Notes */}
      {activeSection === 'visits' && <div className="space-y-4" data-id="element-1262">
          <div className="flex items-center justify-between" data-id="element-1263">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1264">Visit Notes</h3>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm" data-id="element-1265">
              Add Visit Note
            </button>
          </div>
          {mockVisitNotes.map(visit => <div key={visit.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1266">
              <div className="flex items-start justify-between mb-3" data-id="element-1267">
                <div data-id="element-1268">
                  <h4 className="font-semibold text-gray-900" data-id="element-1269">{visit.date}</h4>
                  <p className="text-sm text-gray-600" data-id="element-1270">{visit.doctor}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm" data-id="element-1271">
                <p data-id="element-1272">
                  <strong data-id="element-1273">Chief Complaint:</strong> {visit.complaint}
                </p>
                <p data-id="element-1274">
                  <strong data-id="element-1275">Diagnosis:</strong> {visit.diagnosis}
                </p>
                <p data-id="element-1276">
                  <strong data-id="element-1277">Notes:</strong> {visit.notes}
                </p>
                <p data-id="element-1278">
                  <strong data-id="element-1279">Follow-up:</strong> {visit.followUp}
                </p>
              </div>
            </div>)}
        </div>}
      {/* Prescriptions */}
      {activeSection === 'prescriptions' && <div className="space-y-4" data-id="element-1280">
          <div className="flex items-center justify-between" data-id="element-1281">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1282">
              Prescriptions
            </h3>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-id="element-1283">
              Add Prescription
            </button>
          </div>
          {mockPrescriptions.map(prescription => <div key={prescription.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1284">
              <div className="flex items-start justify-between mb-2" data-id="element-1285">
                <h4 className="font-semibold text-gray-900" data-id="element-1286">
                  {prescription.medication}
                </h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${prescription.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1287">
                  {prescription.status}
                </span>
              </div>
              <div className="text-sm text-gray-700 space-y-1" data-id="element-1288">
                <p data-id="element-1289">
                  <strong data-id="element-1290">Dosage:</strong> {prescription.dosage}
                </p>
                <p data-id="element-1291">
                  <strong data-id="element-1292">Duration:</strong> {prescription.duration}
                </p>
                <p data-id="element-1293">
                  <strong data-id="element-1294">Prescribed by:</strong> {prescription.prescribedBy}
                </p>
                <p data-id="element-1295">
                  <strong data-id="element-1296">Date:</strong> {prescription.date}
                </p>
              </div>
            </div>)}
        </div>}
      {/* Lab Results */}
      {activeSection === 'labs' && <div className="space-y-4" data-id="element-1297">
          <div className="flex items-center justify-between" data-id="element-1298">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1299">Lab Results</h3>
            <button className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm" data-id="element-1300">
              Add Lab Result
            </button>
          </div>
          {mockLabResults.map(lab => <div key={lab.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1301">
              <div className="flex items-start justify-between mb-2" data-id="element-1302">
                <div data-id="element-1303">
                  <h4 className="font-semibold text-gray-900" data-id="element-1304">
                    {lab.testName}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1305">{lab.date}</p>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full" data-id="element-1306">
                  {lab.result}
                </span>
              </div>
              <div className="text-sm text-gray-700" data-id="element-1307">
                <p data-id="element-1308">
                  <strong data-id="element-1309">Performed by:</strong> {lab.performedBy}
                </p>
                <p data-id="element-1310">
                  <strong data-id="element-1311">Notes:</strong> {lab.notes}
                </p>
              </div>
            </div>)}
        </div>}
    </div>;
}